// @-mention support for the messaging composer + message body.
//
// Mentions are stored inline in the plain-text message body (no schema
// change): a named mention is `@[Display Name](profileId)` and the
// broadcast-to-everyone mention is the literal `@everyone`. Both are
// parsed back out for rendering by parseMentionSegments.

export const EVERYONE_ID = 'everyone';

export interface MentionCandidate {
  id: string; // profile_id, or EVERYONE_ID for the "@everyone" pseudo-mention
  display_name: string;
}

export interface MentionTrigger {
  /** Index of the '@' character that opened this in-progress mention. */
  start: number;
  /** Text typed so far after the '@'. */
  query: string;
}

// Is the caret currently sitting inside an "@query" the user is typing?
// The '@' must be at the start of the text or preceded by whitespace so
// things like "email@domain.com" don't trigger the picker, and the query
// itself can't contain whitespace (typing a space closes the mention).
export const detectMentionTrigger = (text: string, caret: number): MentionTrigger | null => {
  const upToCaret = text.slice(0, caret);
  const at = upToCaret.lastIndexOf('@');
  if (at === -1) return null;
  const before = at === 0 ? '' : upToCaret[at - 1];
  if (before && !/\s/.test(before)) return null;
  const query = upToCaret.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
};

export const mentionToken = (candidate: MentionCandidate): string =>
  candidate.id === EVERYONE_ID ? '@everyone' : `@[${candidate.display_name}](${candidate.id})`;

// Replace the in-progress "@query" with the chosen candidate's token
// (plus a trailing space) and report where the caret should land.
export const applyMention = (
  text: string,
  trigger: MentionTrigger,
  candidate: MentionCandidate,
): { text: string; caret: number } => {
  const end = trigger.start + 1 + trigger.query.length;
  const insertion = `${mentionToken(candidate)} `;
  return {
    text: text.slice(0, trigger.start) + insertion + text.slice(end),
    caret: trigger.start + insertion.length,
  };
};

export const filterMentionCandidates = (
  candidates: MentionCandidate[],
  query: string,
): MentionCandidate[] => {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return candidates;
  return candidates.filter((c) => c.display_name.toLowerCase().includes(q));
};

const NAMED_MENTION_RE = /@\[([^\]]+)\]\(([^)]+)\)/;
const EVERYONE_RE = /@everyone\b/i;
const COMBINED_RE = new RegExp(`${NAMED_MENTION_RE.source}|${EVERYONE_RE.source}`, 'gi');

export type MentionSegment =
  | { kind: 'text'; value: string }
  | { kind: 'mention'; displayName: string; profileId: string }
  | { kind: 'everyone' };

// Split a raw message body into plain-text runs and mention tokens, for
// components to render mentions as highlighted chips instead of raw markup.
export const parseMentionSegments = (body: string): MentionSegment[] => {
  const segments: MentionSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  COMBINED_RE.lastIndex = 0;
  while ((match = COMBINED_RE.exec(body)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', value: body.slice(lastIndex, match.index) });
    }
    if (match[1] !== undefined) {
      segments.push({ kind: 'mention', displayName: match[1], profileId: match[2] });
    } else {
      segments.push({ kind: 'everyone' });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ kind: 'text', value: body.slice(lastIndex) });
  }
  return segments;
};

// Plain-text rendering of a body for previews that can't show the
// highlighted chip (reply quotes, composer "replying to" chip) — named
// mentions collapse to "@Display Name", @everyone stays as-is.
export const stripMentionMarkup = (body: string): string =>
  parseMentionSegments(body)
    .map((s) => {
      if (s.kind === 'text') return s.value;
      if (s.kind === 'everyone') return '@everyone';
      return `@${s.displayName}`;
    })
    .join('');

// Does this body mention a specific profile (directly, or via @everyone)?
// Used to give "you were mentioned" a stronger highlight than a mention of
// someone else in the same message.
export const mentionsProfile = (body: string, profileId: string): boolean =>
  parseMentionSegments(body).some(
    (s) => s.kind === 'everyone' || (s.kind === 'mention' && s.profileId === profileId),
  );
